<template>
  <WidgetChart
    v-if="!isLoading"
    title=""
    actionLabel="CREAR"
  >
    <VueUiXy
      :dataset="dataset"
      :config="config"
    />
  </WidgetChart>
</template>

<script setup>
import {
  ref,
  onMounted,
  watch,
} from 'vue';
import { VueUiXy } from 'vue-data-ui';
import useApi from '@/shared/composables/useApi';
import WidgetChart from './base/chart.vue';
import {
  applyConfingTexts,
  buildDateRangeQueryParams,
} from './utils';

const { isLoading, apiGet } = useApi();

const props = defineProps({
  filters: Object,
});

const dataset = ref([
  {
    name: 'Ventas',
    series: [1, 2, 3, 5, 8, 13, 21, 13, 20, 20, 23, 22, 21],
    color: '#6376DD',
    type: 'line',
    shape: 'circle',
    useArea: true,
    useProgression: false,
    dataLabels: true,
    smooth: true,
    dashed: false,
    useTag: 'none',
  },
]);

const config = ref(
  applyConfingTexts({
    theme: '',
    responsive: false,
    customPalette: [],
    useCssAnimation: true,
    downsample: {
      threshold: 500,
    },
    tooltipCustomFormat: {
      show: true,
      formatter: ({ datapoint }) => {
        return `my format: ${datapoint.toLocaleString(navigator.language)}`;
      },
    },
    chart: {
      fontFamily: 'inherit',
      backgroundColor: '#FFFFFFff',
      color: '#1A1A1Aff',
      height: 600,
      width: 1000,
      zoom: {
        show: true,
        color: '#CCCCCCff',
        highlightColor: '#4A4A4A',
        fontSize: 14,
        useResetSlot: false,
        startIndex: null,
        endIndex: null,
        enableRangeHandles: true,
        enableSelectionDrag: false,
        minimap: {
          show: true,
          smooth: false,
          selectedColor: '#1F77B4',
          selectedColorOpacity: 0.2,
          lineColor: '#1A1A1A',
          selectionRadius: 2,
          indicatorColor: '#1A1A1A',
          verticalHandles: false,
        },
      },
      padding: {
        top: 36,
        right: 24,
        bottom: 120,
        left: 120,
      },
      highlighter: {
        color: '#1A1A1Aff',
        opacity: 5,
        useLine: false,
        lineDasharray: 2,
        lineWidth: 1,
      },
      highlightArea: {
        show: false,
        from: 0,
        to: 0,
        color: '#CCCCCCff',
        opacity: 20,
        caption: {
          text: 'Caption',
          fontSize: 20,
          color: '#1A1A1Aff',
          bold: false,
          offsetY: 0,
          width: 'auto',
          padding: 3,
          textAlign: 'center',
        },
      },
      timeTag: {
        show: false,
        backgroundColor: '#e1e5e8ff',
        color: '#1A1A1Aff',
        fontSize: 12,
        circleMarker: {
          radius: 3,
          color: '#1A1A1Aff',
        },
      },
      grid: {
        stroke: '#e1e5e8ff',
        showVerticalLines: false,
        showHorizontalLines: false,
        position: 'middle',
        frame: {
          show: false,
          stroke: '#E1E5E8ff',
          strokeWidth: 2,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          strokeDasharray: 0,
        },
        labels: {
          show: true,
          color: '#1A1A1Aff',
          fontSize: 16,
          axis: {
            yLabel: '',
            yLabelOffsetX: 0,
            xLabel: '',
            xLabelOffsetY: 14,
            fontSize: 12,
          },
          zeroLine: {
            show: true,
          },
          xAxis: {
            showBaseline: true,
          },
          yAxis: {
            showBaseline: true,
            commonScaleSteps: 10,
            useIndividualScale: false,
            stacked: false,
            gap: 12,
            labelWidth: 40,
            formatter: null,
            scaleMin: null,
            scaleMax: null,
          },
          xAxisLabels: {
            color: '#1A1A1Aff',
            show: true,
            values: [],
            fontSize: 18,
            showOnlyFirstAndLast: false,
            showOnlyAtModulo: false,
            modulo: 12,
            yOffset: 8,
            rotation: -45,
          },
        },
      },
      comments: {
        show: true,
        showInTooltip: true,
        width: 200,
        offsetX: 0,
        offsetY: 0,
      },
      labels: {
        fontSize: 16,
        prefix: 'U$D',
        suffix: '',
        value: {
          formatter: ({ value, config }) => {
            return `my format: ${value.toLocaleString(navigator.language)}`;
          },
        },
      },
      legend: {
        color: '#1A1A1Aff',
        show: false,
        fontSize: 16,
      },
      title: {
        text: 'Ventas',
        color: '#1A1A1Aff',
        fontSize: 20,
        bold: true,
        textAlign: 'center',
        paddingLeft: 0,
        paddingRight: 0,
        subtitle: {
          color: '#CCCCCCff',
          text: '',
          fontSize: 16,
          bold: false,
        },
        show: true,
      },
      tooltip: {
        show: true,
        color: '#1A1A1Aff',
        backgroundColor: '#FFFFFFff',
        fontSize: 14,
        customFormat: null,
        borderRadius: 4,
        borderColor: '#e1e5e8',
        borderWidth: 1,
        backgroundOpacity: 30,
        position: 'center',
        offsetY: 24,
        showTimeLabel: true,
        showValue: true,
        showPercentage: false,
        roundingValue: 0,
        roundingPercentage: 0,
      },
      userOptions: {
        show: true,
        showOnChartHover: false,
        keepStateOnChartLeave: true,
        position: 'right',
        buttons: {
          tooltip: true,
          pdf: true,
          csv: true,
          img: true,
          table: true,
          labels: true,
          fullscreen: true,
          sort: false,
          stack: true,
          animation: false,
          annotator: true,
        },
        buttonTitles: {
          open: 'Open options',
          close: 'Close options',
          tooltip: 'Toggle tooltip',
          pdf: 'Download PDF',
          csv: 'Download CSV',
          img: 'Download PNG',
          table: 'Toggle table',
          labels: 'Toggle labels',
          fullscreen: 'Toggle fullscreen',
          stack: 'Toggle stack mode',
          annotator: 'Toggle annotator',
        },
      },
    },
    line: {
      radius: 10,
      useGradient: true,
      strokeWidth: 5,
      dot: {
        hideAboveMaxSerieLength: 62,
        useSerieColor: false,
        fill: '#FFFFFF',
        strokeWidth: 2,
      },
      labels: {
        show: true,
        offsetY: -16,
        rounding: 0,
        color: '#1A1A1Aff',
        formatter: ({ value, config }) => {
          return `${value.toFixed(2).toLocaleString(navigator.language, {
            style: 'currency',
            // currency: 'U$D'
          })}`;
        },
      },
      area: {
        useGradient: true,
        opacity: 20,
      },
    },
    table: {
      responsiveBreakpoint: 400,
      rounding: 0,
      sparkline: true,
      showSum: true,
      columnNames: {
        period: 'Period',
        total: 'Total',
      },
      th: {
        backgroundColor: '#FAFAFAff',
        color: '#1A1A1Aff',
        outline: '',
      },
      td: {
        backgroundColor: '#FAFAFAff',
        color: '#1A1A1Aff',
        outline: '',
      },
    },
    showTable: false,
  }),
);

const fetchData = async () => {
  const queryParams = buildDateRangeQueryParams(props.filters);
  const result = await apiGet('/dashboard/ventas', { params: queryParams });

  config.value.chart.grid.labels.xAxisLabels.values = result.data.labels;
  const raw = result.data.values;
  const cumulative = raw.map((_, i) => raw.slice(0, i + 1).reduce((sum, x) => sum + (x ?? 0), 0));
  dataset.value[0].series = cumulative;
};

onMounted(() => {
  fetchData();
});

watch(
  () => props.filters,
  () => {
    fetchData();
  },
  { deep: true },
);
</script>
